import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import {
  Button,
  MessageBar,
  MessageBarBody,
  MessageBarActions,
  Spinner,
} from '@fluentui/react-components';
import type { SessionResponse, SessionStatus } from '../../api/src/shared/contract';
import { ApiClientError, getSession } from '../api/client';
import { readMessage, type MessageSnapshot } from '../office/mailbox';
import { Header } from './Header';
import { usePaneStyles } from './styles';
import { TicketForm } from './TicketForm';

// Settings is a separate view; keep it out of the initial chunk so cold start pays for
// the form path first. At 512 MB / 0.25 CPU, bundle size is a latency cost.
const Settings = lazy(async () => {
  const mod = await import('./Settings');
  return { default: mod.Settings };
});

type View = 'form' | 'settings';

type LoadState =
  | { state: 'loading' }
  | { state: 'ready'; session: SessionResponse; message: MessageSnapshot }
  | { state: 'error'; error: string };

/**
 * Loads the Office message and /api/session together, then either shows the ticket form,
 * a blocker explaining why filing is impossible, or Settings.
 */
export function App() {
  const styles = usePaneStyles();
  const [load, setLoad] = useState<LoadState>({ state: 'loading' });
  const [view, setView] = useState<View>('form');
  const [activeProviderId, setActiveProviderId] = useState<string | null>(null);

  const reload = useCallback(async (providerId?: string | null) => {
    setLoad({ state: 'loading' });
    try {
      const [message, session] = await Promise.all([readMessage(), getSession(providerId)]);
      setLoad({ state: 'ready', message, session });
      if (session.providerId) setActiveProviderId(session.providerId);
    } catch (error) {
      const message =
        error instanceof ApiClientError
          ? error.message
          : error instanceof Error
            ? error.message
            : String(error);
      setLoad({ state: 'error', error: message });
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  if (load.state === 'loading') {
    return <Spinner label="Loading…" style={{ padding: 24 }} />;
  }

  if (load.state === 'error') {
    return (
      <div className={styles.root}>
        <Header title="Create ticket" />
        <div className={styles.body}>
          <MessageBar intent="error">
            <MessageBarBody>{load.error}</MessageBarBody>
            <MessageBarActions>
              <Button size="small" onClick={() => void reload(activeProviderId)}>
                Retry
              </Button>
            </MessageBarActions>
          </MessageBar>
        </div>
      </div>
    );
  }

  const { session, message } = load;

  if (view === 'settings') {
    return (
      <Suspense fallback={<Spinner label="Loading settings…" style={{ padding: 24 }} />}>
        <Settings
          session={session}
          onBack={() => setView('form')}
          onSessionChange={(next) => setLoad({ state: 'ready', message, session: next })}
          onReload={() => reload(session.providerId)}
        />
      </Suspense>
    );
  }

  if (session.status !== 'ok' || !session.directory || !session.providerId) {
    return (
      <BlockedSession
        status={session.status}
        isAdministrator={session.caller.isAdministrator}
        {...(session.message ? { message: session.message } : {})}
        onSettings={() => setView('settings')}
        onRetry={() => void reload(activeProviderId)}
      />
    );
  }

  return (
    <TicketForm
      message={message}
      directory={session.directory}
      preferences={session.preferences}
      providers={session.providers}
      providerId={session.providerId}
      onProviderChange={(id) => void reload(id)}
      onSettings={() => setView('settings')}
    />
  );
}

function BlockedSession({
  status,
  message,
  isAdministrator,
  onSettings,
  onRetry,
}: {
  status: SessionStatus;
  message?: string;
  isAdministrator: boolean;
  onSettings: () => void;
  onRetry: () => void;
}) {
  const styles = usePaneStyles();

  return (
    <div className={styles.root}>
      <Header title="Create ticket" onSettings={onSettings} />
      <div className={styles.body}>
        <MessageBar intent={status === 'no_providers' ? 'info' : 'warning'}>
          <MessageBarBody>
            {message ?? defaultMessage(status)}
            {status === 'no_providers' && isAdministrator ? (
              <>
                {' '}
                <a href="/admin" target="_blank" rel="noreferrer">
                  Open admin
                </a>
              </>
            ) : null}
          </MessageBarBody>
          <MessageBarActions>
            <Button size="small" onClick={onRetry}>
              Retry
            </Button>
          </MessageBarActions>
        </MessageBar>
      </div>
    </div>
  );
}

function defaultMessage(status: SessionStatus): string {
  switch (status) {
    case 'no_providers':
      return 'No service providers are configured yet. Ask an administrator to add one.';
    case 'bad_token':
      return 'SolarWinds rejected a service provider API token. Ask an administrator to update it.';
    case 'not_in_solarwinds':
      return 'No SolarWinds user matches your signed-in email. Ask IT to create your account.';
    case 'not_an_agent':
      return 'You are not an agent in any configured SolarWinds service provider. Ask IT if you need access.';
    case 'no_groups':
      return 'You are not a member of any SolarWinds group, so there is nowhere to assign a ticket.';
    default:
      return 'Unable to file a ticket right now.';
  }
}
